import type {JsonObject,JsonValue,WorldDocument} from './types';
const unsafe = new Set(['__proto__','prototype','constructor']);
/** Always advances, even when a single entry exceeds the page budget. */
export function boundedReadPage(items:readonly unknown[],offset=0,limit=50,maxCharacters=12000):JsonObject {
  if(![offset,limit,maxCharacters].every(Number.isFinite))throw new Error('Invalid read page');
  offset=Math.max(0,Math.trunc(offset));limit=Math.max(1,Math.min(100,Math.trunc(limit)));maxCharacters=Math.max(512,Math.min(48000,Math.trunc(maxCharacters)));
  const entries:JsonValue[]=[];
  for(const item of items.slice(offset,offset+limit)) {
    const candidate=structuredClone(item) as JsonValue;
    if(JSON.stringify([...entries,candidate]).length>maxCharacters-180) {
      if(entries.length)break;
      const record=item&&typeof item==='object'?item as Record<string,unknown>:{};
      entries.push({id:String(record.id??record.path??'').slice(0,160),truncated:true});break;
    }
    entries.push(candidate);
  }
  return {entries,total:items.length,nextOffset:offset+entries.length<items.length?offset+entries.length:null};
}
/** Explicit fields are read-only projections, never silently substituted full documents. */
export function projectWorldRead(document:WorldDocument,paths:readonly string[] = [],maxCharacters=24000):JsonObject {
  if(!Number.isFinite(maxCharacters)) throw new Error('Read budget must be finite');
  const limit=Math.max(512,Math.min(48000,Math.trunc(maxCharacters)));
  if(paths.length>32 || paths.some(path=>!path || path.length>300 || path.split('.').some(key=>!key || unsafe.has(key)) || !/^(fields|runtimeState)\.[^.]+/.test(path))) throw new Error('Read paths must be fields.x or runtimeState.x, at most 32 paths');
  let result:JsonObject;
  if(!paths.length) result=structuredClone(document) as unknown as JsonObject;
  else {
    const projected:JsonObject={};
    for(const path of paths) {
      const keys=path.split('.');let cursor:unknown=document;
      for(const key of keys) { if(!cursor || typeof cursor!=='object' || !Object.prototype.hasOwnProperty.call(cursor,key)) {cursor=undefined;break;} cursor=(cursor as Record<string,unknown>)[key]; }
      if(cursor!==undefined) projected[path]=structuredClone(cursor) as JsonValue;
    }
    result={id:document.id,title:document.title,kind:document.kind,revision:document.revision,projected};
  }
  if(JSON.stringify(result).length<=limit) return result;
  const fallback:JsonObject={id:document.id.slice(0,120),truncated:true,reason:'Result exceeds read budget. Request narrower fieldPaths.',availableFieldPaths:[]};
  const available:string[]=[];
  for (const path of [...Object.keys(document.fields).map(key=>`fields.${key}`),...Object.keys(document.runtimeState??{}).map(key=>`runtimeState.${key}`)]) {
    if (path.length>300 || available.length>=32) continue;
    available.push(path); fallback.availableFieldPaths=available;
    if (JSON.stringify(fallback).length>limit) { available.pop(); break; }
  }
  return fallback;
}
